import type { ForqSupabaseClient } from './client';
import type {
  CreateCreditNoteInput,
  CreateInvoiceInput,
  DunInvoiceInput,
  FinanceGateway,
  Invoice,
  ListInvoicesInput,
  RecordPaymentInput,
  TransitionInvoiceInput,
  UpdateInvoiceInput,
} from '@forq/finance-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

/**
 * Ausgangsrechnungen gegen Supabase.
 *
 * Die RPCs geben die Rechnung als jsonb GENAU in der Form von Invoice zurück (camelCase, Daten als
 * YYYY-MM-DD, Zeitstempel als ISO) -- daher ein Cast statt Feld-für-Feld-Übersetzung; ein Test in
 * finance.test.ts sichert die Gestalt.
 *
 * Wichtig für "wasserdicht": die Rechnungssummen rechnet die DATENBANK aus den Positionen, nicht
 * dieser Adapter. Er schickt die Positionen und bekommt die fertige Rechnung zurück.
 */
export class SupabaseFinanceGateway implements FinanceGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`finance.${functionName}`, messageOf(error));
    return data;
  }

  async listInvoices(input: ListInvoicesInput): Promise<readonly Invoice[]> {
    const data = await this.rpc('finance_invoice_list', {
      p_tenant_id: input.tenantId,
      p_document_status: input.documentStatus ?? 'all',
    });
    return (data ?? []) as readonly Invoice[];
  }

  async createInvoice(input: CreateInvoiceInput): Promise<Invoice> {
    const { tenantId, ...payload } = input;
    return this.rpc('finance_invoice_create', { p_tenant_id: tenantId, p_payload: payload }) as Promise<Invoice>;
  }

  async updateInvoice(input: UpdateInvoiceInput): Promise<Invoice> {
    const { tenantId, invoiceId, ...payload } = input;
    return this.rpc('finance_invoice_update', { p_tenant_id: tenantId, p_invoice_id: invoiceId, p_payload: payload }) as Promise<Invoice>;
  }

  async transitionInvoice(input: TransitionInvoiceInput): Promise<Invoice> {
    return this.rpc('finance_invoice_transition', {
      p_tenant_id: input.tenantId,
      p_invoice_id: input.invoiceId,
      p_to_status: input.toStatus,
      p_reason: input.reason ?? '',
    }) as Promise<Invoice>;
  }

  async recordPayment(input: RecordPaymentInput): Promise<Invoice> {
    return this.rpc('finance_invoice_record_payment', {
      p_tenant_id: input.tenantId,
      p_invoice_id: input.invoiceId,
      p_amount: input.amount,
      p_kind: input.kind ?? 'payment',
      p_value_date: input.valueDate ?? null,
      p_method: input.method ?? '',
      p_note: input.note ?? '',
    }) as Promise<Invoice>;
  }

  async createCreditNote(input: CreateCreditNoteInput): Promise<Invoice> {
    return this.rpc('finance_invoice_credit_note', {
      p_tenant_id: input.tenantId,
      p_invoice_id: input.invoiceId,
    }) as Promise<Invoice>;
  }

  async dunInvoice(input: DunInvoiceInput): Promise<Invoice> {
    return this.rpc('finance_invoice_dun', {
      p_tenant_id: input.tenantId,
      p_invoice_id: input.invoiceId,
      p_fee: input.fee ?? 0,
      p_interest: input.interest ?? 0,
      p_due_date: input.dueDate ?? null,
      p_note: input.note ?? '',
    }) as Promise<Invoice>;
  }
}
